import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  Calendar,
  Building2,
  Target,
  Lightbulb,
  TrendingUp
} from 'lucide-react';
import PublicHeader from './components/PublicHeader';
import PublicFooter from './components/PublicFooter';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PortfolioDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [relatedProjects, setRelatedProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Default project data for demonstration
  const defaultProjects = {
    'velux-brand-campaign': {
      id: '1',
      title: 'VELUX Brand Campaign',
      slug: 'velux-brand-campaign',
      client_name: 'VELUX North America',
      description: 'Comprehensive brand repositioning and integrated marketing campaign driving category growth and brand awareness.',
      challenge: 'VELUX needed to differentiate themselves in a crowded market while driving both consumer awareness and dealer engagement across multiple channels.',
      solution: 'We developed an integrated marketing strategy combining consumer advertising, trade marketing, digital campaigns, and influencer partnerships to create a cohesive brand experience.',
      results: 'The campaign drove significant increases in brand awareness, dealer engagement, and qualified lead generation, establishing VELUX as the premium choice in their category.',
      featured_image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&h=800&fit=crop',
      gallery_images: [
        'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=600&fit=crop'
      ],
      category: 'Brand Strategy',
      technologies: ['Brand Identity', 'Digital Marketing', 'Print Advertising', 'Trade Marketing', 'Influencer Programs'],
      is_featured: true
    },
    'cooper-tools-digital': {
      id: '2',
      title: 'Cooper Tools Digital Transformation',
      slug: 'cooper-tools-digital',
      client_name: 'Cooper Hand Tools',
      description: 'Complete digital presence overhaul including e-commerce, product catalogs, and dealer portal systems.',
      challenge: 'Cooper Hand Tools needed to modernize their digital presence while supporting multiple brands and thousands of SKUs across various sales channels.',
      solution: 'We created a comprehensive digital ecosystem including responsive websites, product catalog systems, dealer portals, and marketing automation workflows.',
      results: 'The digital transformation improved dealer efficiency, increased online engagement, and streamlined product information delivery across all channels.',
      featured_image: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=1200&h=800&fit=crop',
      gallery_images: [
        'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=600&fit=crop'
      ],
      category: 'Web Development',
      technologies: ['Web Design', 'E-commerce', 'CMS', 'Product Database', 'Dealer Portal'],
      is_featured: true
    },
    'john-deere-launch': {
      id: '3',
      title: 'John Deere Product Launch',
      slug: 'john-deere-launch',
      client_name: 'John Deere/Homelite',
      description: 'Multi-channel product launch campaign including packaging design, merchandising, and trade show presence.',
      challenge: 'Launch a new product line with maximum impact across retail, dealer, and trade channels simultaneously.',
      solution: 'Developed comprehensive launch materials including packaging, point-of-sale displays, trade show exhibits, and integrated marketing communications.',
      results: 'Successful multi-channel launch that exceeded initial sales targets and established strong retail presence.',
      featured_image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=800&fit=crop',
      gallery_images: [],
      category: 'Product Marketing',
      technologies: ['Packaging', 'Merchandising', 'Event Marketing', 'Trade Shows'],
      is_featured: true
    }
  };

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`${API}/public/portfolio/${slug}`);
        setProject(response.data);
      } catch (err) {
        // Use default data if API fails
        if (defaultProjects[slug]) {
          setProject(defaultProjects[slug]);
        } else {
          navigate('/portfolio');
        }
      } finally {
        setLoading(false);
      }
    };

    const fetchRelated = async () => {
      try {
        const response = await axios.get(`${API}/public/portfolio?limit=3`);
        setRelatedProjects(response.data.filter(p => p.slug !== slug).slice(0, 3));
      } catch (err) {
        const related = Object.values(defaultProjects).filter(p => p.slug !== slug).slice(0, 3);
        setRelatedProjects(related);
      }
    };

    fetchProject();
    fetchRelated();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader />
        <div className="pt-40 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3C4F]"></div>
        </div>
      </div>
    );
  }

  if (!project) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero Section */}
      <section className="pt-40 pb-8 lg:pt-48">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Link 
            to="/portfolio" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-[#1F3C4F] mb-8 transition-colors"
            data-testid="back-to-portfolio"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Portfolio
          </Link>
          
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              {project.category && (
                <Badge className="mb-4 bg-[#1F3C4F]">{project.category}</Badge>
              )}
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight mb-6">
                {project.title}
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed mb-8">
                {project.description}
              </p>
              
              {project.client_name && (
                <div className="flex items-center gap-3 mb-6 text-slate-600">
                  <Building2 className="w-5 h-5" />
                  <span className="font-medium">{project.client_name}</span>
                </div>
              )}

              {project.technologies && project.technologies.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech, idx) => (
                    <span key={idx} className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm">
                      {tech}
                    </span>
                  ))}
                </div>
              )}

              {project.project_url && (
                <a 
                  href={project.project_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-6 text-[#1F3C4F] font-medium hover:underline"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Live Project
                </a>
              )}
            </div>
            
            <div className="relative">
              <img 
                src={project.featured_image || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=800&fit=crop'}
                alt={project.title}
                className="w-full rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Challenge / Solution / Results */}
      {(project.challenge || project.solution || project.results) && (
        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8">
              {project.challenge && (
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-8">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center text-red-600 mb-6">
                      <Target className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-4">The Challenge</h3>
                    <p className="text-slate-600 leading-relaxed">{project.challenge}</p>
                  </CardContent>
                </Card>
              )}
              
              {project.solution && (
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-8">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 mb-6">
                      <Lightbulb className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-4">Our Solution</h3>
                    <p className="text-slate-600 leading-relaxed">{project.solution}</p>
                  </CardContent>
                </Card>
              )}
              
              {project.results && (
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-8">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-green-600 mb-6">
                      <TrendingUp className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-4">The Results</h3>
                    <p className="text-slate-600 leading-relaxed">{project.results}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {project.gallery_images && project.gallery_images.length > 0 && (
        <section className="py-16 lg:py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">Project Gallery</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {project.gallery_images.map((image, idx) => (
                <div key={idx} className="aspect-[4/3] rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src={image}
                    alt={`${project.title} - Image ${idx + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">Related Projects</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedProjects.map((related) => (
                <Link 
                  key={related.id} 
                  to={`/portfolio/${related.slug}`}
                  data-testid={`related-${related.slug}`}
                >
                  <Card className="border-0 shadow-lg overflow-hidden group cursor-pointer hover:shadow-xl transition-shadow">
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img 
                        src={related.featured_image || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop'} 
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-6">
                      {related.category && (
                        <Badge variant="outline" className="mb-3">{related.category}</Badge>
                      )}
                      <h3 className="text-lg font-bold text-slate-900 group-hover:text-[#1F3C4F] transition-colors">
                        {related.title}
                      </h3>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-[#1F3C4F]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can bring the same strategic approach to your marketing challenges.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-[#1F3C4F] hover:bg-slate-100 text-lg px-8 py-6">
              Get in Touch
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default PortfolioDetailPage;
